import type { Entry, Place } from "./types";

/* -------------------------------------------------------------------------- */
/* Dates                                                                      */
/* -------------------------------------------------------------------------- */

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

const MONTHS_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/** "Today", "Yesterday", "4 days ago", "12 March", "12 March 2023". */
export function relativeDate(iso: string | null | undefined, now = new Date()): string {
  const date = parse(iso);
  if (!date) return "Undated";

  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);

  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 14) return "Last week";
  if (days < 35) return `${Math.round(days / 7)} weeks ago`;

  const month = MONTHS_LONG[date.getMonth()] ?? "";
  const sameYear = date.getFullYear() === now.getFullYear();
  return sameYear
    ? `${date.getDate()} ${month}`
    : `${date.getDate()} ${month} ${date.getFullYear()}`;
}

/**
 * The rubber-stamp treatment: "12 · MAR · 2026". Uppercase and spaced out, the
 * way a date stamp actually prints.
 */
export function stampedDate(iso: string | null | undefined): string {
  const date = parse(iso);
  if (!date) return "UNDATED";
  const day = String(date.getDate()).padStart(2, "0");
  const month = (MONTHS_SHORT[date.getMonth()] ?? "").toUpperCase();
  return `${day} · ${month} · ${date.getFullYear()}`;
}

/** "12 March 2026" — for label blocks and title attributes. */
export function longDate(iso: string | null | undefined): string {
  const date = parse(iso);
  if (!date) return "Undated";
  return `${date.getDate()} ${MONTHS_LONG[date.getMonth()] ?? ""} ${date.getFullYear()}`;
}

/** A precise machine-readable value for `<time dateTime>`. */
export function isoDate(iso: string | null | undefined): string | undefined {
  const date = parse(iso);
  return date ? date.toISOString() : undefined;
}

/** "12 March 2026 at 14:05" — used in the "collected" line. */
export function longDateTime(iso: string | null | undefined): string {
  const date = parse(iso);
  if (!date) return "Undated";
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${longDate(iso)} at ${hours}:${minutes}`;
}

/* -------------------------------------------------------------------------- */
/* Confidence                                                                 */
/* -------------------------------------------------------------------------- */

export type ConfidenceWording = {
  /** "Confident", "Fairly sure", … */
  label: string;
  /** A full sentence for the field note. */
  sentence: string;
  /** 0..100, rounded, for the numeric aside. */
  percent: number;
  /** Whether the identification deserves a visible caveat. */
  tentative: boolean;
};

/**
 * Confidence is a number the model produced; a person reads words. Always show
 * the words first, the number second.
 */
export function confidenceWording(confidence: number | null): ConfidenceWording | null {
  if (confidence === null || Number.isNaN(confidence)) return null;
  const clamped = Math.min(1, Math.max(0, confidence));
  const percent = Math.round(clamped * 100);

  if (clamped >= 0.9) {
    return {
      label: "Certain",
      sentence: "A confident identification — the field marks are unambiguous.",
      percent,
      tentative: false,
    };
  }
  if (clamped >= 0.75) {
    return {
      label: "Confident",
      sentence: "A confident identification, though worth a second look in person.",
      percent,
      tentative: false,
    };
  }
  if (clamped >= 0.55) {
    return {
      label: "Fairly sure",
      sentence: "Fairly sure, but this one has close relatives that photograph alike.",
      percent,
      tentative: true,
    };
  }
  if (clamped >= 0.35) {
    return {
      label: "Tentative",
      sentence: "Tentative. Treat this as a first guess rather than a determination.",
      percent,
      tentative: true,
    };
  }
  return {
    label: "A guess",
    sentence: "Little more than a guess — another photograph would help enormously.",
    percent,
    tentative: true,
  };
}

/* -------------------------------------------------------------------------- */
/* Places                                                                     */
/* -------------------------------------------------------------------------- */

/** The one-line place, preferring the server's own label. */
export function placeLine(place: Place | null | undefined): string | null {
  if (!place) return null;
  if (place.label && place.label.trim().length > 0) return place.label;

  const parts = [place.locality, place.admin1, place.country].filter(
    (part): part is string => typeof part === "string" && part.trim().length > 0,
  );
  if (parts.length === 0) return null;
  return parts.join(", ");
}

export function hasPlace(entry: Entry): boolean {
  return placeLine(entry.place) !== null;
}

/** How the place was arrived at, in plain words. */
export function placeSourceNote(place: Place): string | null {
  switch (place.source) {
    case "exif":
      return "Read from the photograph";
    case "manual":
      return "Set by you";
    case "default":
      return "Your default location";
    case "none":
      return null;
    default:
      return null;
  }
}

/* -------------------------------------------------------------------------- */
/* Misc                                                                       */
/* -------------------------------------------------------------------------- */

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** "7 of 16 trees found" — the progress band's whole job. */
export function progressLine(found: number, total: number, plural: string): string {
  if (total === 0) return found > 0 ? `${found} found` : "Nothing on the list yet";
  return `${found} of ${total} ${plural} found`;
}

export function pluralise(count: number, singular: string, plural?: string): string {
  const word = count === 1 ? singular : (plural ?? `${singular}s`);
  return `${count} ${word}`;
}

/**
 * Roman numerals, for field-guide plate numbers. A 1950s guide would never
 * have printed "Plate 112".
 */
export function toRoman(value: number): string {
  const numerals: readonly [number, string][] = [
    [1000, "M"],
    [900, "CM"],
    [500, "D"],
    [400, "CD"],
    [100, "C"],
    [90, "XC"],
    [50, "L"],
    [40, "XL"],
    [10, "X"],
    [9, "IX"],
    [5, "V"],
    [4, "IV"],
    [1, "I"],
  ];
  let remaining = Math.max(0, Math.floor(value));
  let out = "";
  for (const [amount, numeral] of numerals) {
    while (remaining >= amount) {
      out += numeral;
      remaining -= amount;
    }
  }
  return out || "—";
}

/** Rarity in words, 1..5. Kept here so the badge and the checklist agree. */
const RARITY_WORDS = ["Everywhere", "Common", "Uncommon", "Scarce", "Rare"] as const;

export function rarityWord(rarity: number | null): string | null {
  if (rarity === null) return null;
  const index = Math.min(5, Math.max(1, Math.round(rarity))) - 1;
  return RARITY_WORDS[index] ?? null;
}
